import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ILocalizedInformation, ITheme, ITotalPriceSummary } from '@inter-ikea-kompis/types';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { Text } from '@inter-ikea-kompis/component-text';

import * as SkapaStyle from '@ingka/common-styles-webc';

import type IEcoFeeDexfSettings from '../../types/IEcoFeeDexfSettings.js';
import type IEcoFeeTranslations from '../../types/IEcoFeeTranslations.js';
import EcoFeeCardStyle from './EcoFeeCardStyle.js';

/**
 * Eco fee card component.
 */

export default class EcoFeeCard extends KompisElement {
	// Accepts a object format corresponding to { ShoppingProduct } from '@inter-ikea-kompis/types';
	@property({ type: Object })
	public totalPrice: ITotalPriceSummary | null = null;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IEcoFeeTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IEcoFeeDexfSettings | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	private dexfSettings: IEcoFeeDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		if (!this.totalPrice || !this.getSettings().kompis.localization.hasEcoPart) {
			return null;
		}

		const translations = this.getTranslations();

		return html`
			<style>
				${EcoFeeCardStyle.getStyle({
					theme: this.theme
				})}
			</style>

			<div class="container">
				${this.getEcoText(translations)} ${this.getElectronicEcoText(translations)}
				${this.getLink()}

				<div class="details">
					${this.getEcoExcludingTaxPrice(translations)} ${this.getEcoPrice(translations)}
					${this.getWeeePrice(translations)}
					<div class="line"></div>
					${this.getEcoTotalPrice(translations)}
				</div>
			</div>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-price-formatter', PriceFormatter);
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}

		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IEcoFeeDexfSettings {
		return <IEcoFeeDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IEcoFeeTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IEcoFeeTranslations>translations;
	}

	private getEcoText(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice?.ecoPrice) {
			return null;
		}

		return html`
			<h3 class="headline ${SkapaStyle.Text.classnames({ heading: { xs: true } })}">
				<b>${translations.ecoTextHeadline}</b>
			</h3>
			<p class="about no-margin ${SkapaStyle.Text.classnames({ body: { m: true } })}">
				${translations.ecoText}
			</p>
		`;
	}

	private getElectronicEcoText(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice?.weeePrice) {
			return null;
		}

		return html`
			<h3 class="headline ${SkapaStyle.Text.classnames({ heading: { xs: true } })}">
				${translations.ecoHeadlineElectronics}
			</h3>
			<p class="about no-margin ${SkapaStyle.Text.classnames({ body: { m: true } })}">
				${translations.ecoTextElectronics}
			</p>
		`;
	}
	private getLink(): RenderResult {
		const ecoLinkText = this.getTranslations().ecoLinkText;
		const urls = this.getSettings().kompis.urls;
		if (this.disableLinks || !urls.ecoLinkURL) {
			return null;
		}

		return html`
			<kompis-text class="link" .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodyMedium}">
				<kompis-hyperlink
					.theme="${this.theme}"
					.text="${ecoLinkText}"
					.color="${HyperlinkColorEnum.black}"
					.href="${urls.ecoLinkURL}"
					.target="${'_blank'}"
					.underline="${true}"
				>
				</kompis-hyperlink>
			</kompis-text>
		`;
	}

	private getEcoExcludingTaxPrice(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice) {
			return null;
		}

		return html`
			<div class="row subPrice">
				<kompis-text
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
					aria-hidden="true"
				>
					${translations.ecoPriceExcluding}
				</kompis-text>
				<kompis-text
					class="price"
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
				>
					<kompis-price-formatter
						.settings="${this.settings}"
						.translations="${this.translations}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${this.getPrice(this.totalPrice)}"
						.ariaLabelPrice="${translations.ecoPriceExcluding}"
					></kompis-price-formatter>
				</kompis-text>
			</div>
		`;
	}

	private getEcoPrice(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice?.ecoPrice) {
			return null;
		}

		return html`
			<div class="row subPrice">
				<kompis-text
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
					aria-hidden="true"
				>
					${translations.ecoPrice}
				</kompis-text>
				<kompis-text
					class="price"
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
				>
					<kompis-price-formatter
						.settings="${this.settings}"
						.translations="${this.translations}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${this.totalPrice.ecoPrice}"
						.ariaLabelPrice="${translations.ecoPrice}"
					></kompis-price-formatter>
				</kompis-text>
			</div>
		`;
	}

	private getWeeePrice(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice?.weeePrice) {
			return null;
		}

		return html`
			<div class="row subPrice">
				<kompis-text
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
					aria-hidden="true"
				>
					${translations.ecoHeadlineElectronics}
				</kompis-text>
				<kompis-text
					class="price"
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
				>
					<kompis-price-formatter
						.settings="${this.settings}"
						.translations="${this.translations}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${this.totalPrice.weeePrice}"
						.ariaLabelPrice="${translations.ecoHeadlineElectronics}"
					>
					</kompis-price-formatter>
				</kompis-text>
			</div>
		`;
	}

	private getEcoTotalPrice(translations: IEcoFeeTranslations): RenderResult {
		if (!this.totalPrice?.inclTax) {
			return null;
		}

		return html`
			<div class="row total">
				<kompis-text
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
					.bold="${true}"
					aria-hidden="true"
				>
					${translations.ecoPriceTotal}
				</kompis-text>
				<kompis-text
					class="price"
					.type="${ThemeFontStyleTypeEnum.bodyMedium}"
					.theme="${this.theme}"
					.bold="${true}"
				>
					<kompis-price-formatter
						.settings="${this.settings}"
						.translations="${this.translations}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${this.totalPrice.inclTax}"
						.ariaLabelPrice="${translations.ecoPriceTotal}"
					></kompis-price-formatter>
				</kompis-text>
			</div>
		`;
	}

	/**
	 * Calculates price for eco & weee price, weee price or eco price.
	 *
	 * @param totalPrice The non-null total price property.
	 * @returns Price.
	 */
	private getPrice(totalPrice: ITotalPriceSummary): number {
		if (totalPrice.ecoPrice && totalPrice.weeePrice) {
			return totalPrice.inclTax - totalPrice.ecoPrice - totalPrice.weeePrice; // Weee and eco price
		}

		if (totalPrice.weeePrice) {
			return totalPrice.inclTax - totalPrice.weeePrice; // Weee price
		}

		return totalPrice.inclTax - totalPrice.ecoPrice; // Eco price
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-eco-fee-card': EcoFeeCard;
	}
}
